// A CS:S first-person weapon as a three.js skinned mesh that plays the model's own sequences.
// Lives in Source model space (X forward, Y left, Z up); the viewmodel turns that into camera space.

import * as THREE from 'three';
import type { WeaponAssets, WeaponMaterial, WeaponTexture } from '../models/cssWeapons';
import type { StudioModel, StudioSequence } from '../models/studio';

const tmpQ = new Float32Array(4);

function makeTexture(t: WeaponTexture, color: boolean, anisotropy: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(t.data, t.width, t.height, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = anisotropy;
  if (color) tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Shared textures for both weapons (the hands texture is used by both). */
export class WeaponTextures {
  private readonly cache = new Map<string, THREE.DataTexture>();
  constructor(
    private readonly assets: WeaponAssets,
    private readonly anisotropy: number,
  ) {}
  get(key: string | null, color: boolean): THREE.DataTexture | null {
    if (!key || !this.assets.textures[key]) return null;
    const k = key + (color ? ':c' : ':l');
    let t = this.cache.get(k);
    if (!t) {
      t = makeTexture(this.assets.textures[key], color, this.anisotropy);
      this.cache.set(k, t);
    }
    return t;
  }
  dispose() {
    for (const t of this.cache.values()) t.dispose();
  }
}

export class StudioWeapon {
  /** Source model space root. */
  readonly group = new THREE.Group();
  private readonly bones: THREE.Bone[] = [];
  private readonly model: StudioModel;
  private readonly byName = new Map<string, StudioSequence>();
  private seq: StudioSequence;
  private time = 0;
  private rate = 1;
  private idle: StudioSequence;
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];

  constructor(m: StudioModel, materials: WeaponMaterial[], textures: WeaponTextures) {
    this.model = m;
    for (const b of m.bones) {
      const bone = new THREE.Bone();
      bone.name = b.name;
      bone.position.fromArray(b.pos);
      bone.quaternion.fromArray(b.quat);
      this.bones.push(bone);
    }
    m.bones.forEach((b, i) => (b.parent >= 0 ? this.bones[b.parent] : this.group).add(this.bones[i]));
    const inverses = m.bones.map((b) => {
      const p = b.poseToBone;
      return new THREE.Matrix4().set(
        p[0],
        p[1],
        p[2],
        p[3],
        p[4],
        p[5],
        p[6],
        p[7],
        p[8],
        p[9],
        p[10],
        p[11],
        0,
        0,
        0,
        1,
      );
    });
    const skeleton = new THREE.Skeleton(this.bones, inverses);

    const mats = materials.map((mt) => {
      const mat = new THREE.MeshPhongMaterial({
        map: textures.get(mt.base, true),
        normalMap: textures.get(mt.normal, false),
        specularMap: textures.get(mt.mask, false),
        specular: mt.mask ? 0x666666 : 0x181818,
        shininess: mt.mask ? 60 : 12,
      });
      // Source normal maps are DirectX style (green points down).
      if (mat.normalMap) mat.normalScale.set(1, -1);
      this.materials.push(mat);
      return mat;
    });
    for (const me of m.meshes) {
      if (me.model !== 0) continue;
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(me.positions, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(me.normals, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(me.uvs, 2));
      g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(me.boneIndices, 4));
      g.setAttribute('skinWeight', new THREE.BufferAttribute(me.boneWeights, 4));
      g.setIndex(new THREE.BufferAttribute(me.indices, 1));
      this.geometries.push(g);
      const mesh = new THREE.SkinnedMesh(g, mats[me.texture] ?? mats[0]);
      mesh.frustumCulled = false;
      this.group.add(mesh);
      mesh.bind(skeleton, new THREE.Matrix4());
    }

    for (const s of m.sequences) if (!this.byName.has(s.name)) this.byName.set(s.name, s);
    this.idle = this.byName.get('idle') ?? m.sequences[0];
    this.seq = this.idle;
  }

  has(name: string) {
    return this.byName.has(name);
  }

  /** Duration of a sequence in seconds at normal speed. */
  duration(name: string): number {
    const s = this.byName.get(name);
    return s ? s.frames / s.fps : 0;
  }

  /** Sequence to loop when nothing else plays. */
  setIdle(name: string) {
    const s = this.byName.get(name);
    if (!s) return;
    const wasIdle = this.seq === this.idle;
    this.idle = s;
    if (wasIdle) this.play(name);
  }

  /** Play a sequence from the start; `seconds` stretches it to that length. Then back to idle. */
  play(name: string, seconds?: number) {
    const s = this.byName.get(name);
    if (!s) return;
    this.seq = s;
    this.time = 0;
    const d = s.frames / s.fps;
    this.rate = seconds && seconds > 0 && d > 0 ? d / seconds : 1;
  }

  /** Bone by name (for attachments). */
  bone(i: number): THREE.Bone | undefined {
    return this.bones[i];
  }

  attachment(name: string) {
    return this.model.attachments.find((a) => a.name === name) ?? null;
  }

  update(dt: number) {
    let s = this.seq;
    this.time += dt * this.rate;
    let f = this.time * s.fps;
    if (f >= s.frames - 1) {
      if (s === this.idle) {
        f = s.frames > 1 ? f % (s.frames - 1) : 0;
        this.time = f / s.fps;
      } else {
        // Done: back to idle.
        this.seq = s = this.idle;
        this.time = 0;
        this.rate = 1;
        f = 0;
      }
    }
    const f0 = Math.min(s.frames - 1, Math.floor(f));
    const f1 = Math.min(s.frames - 1, f0 + 1);
    const t = f - f0;
    const n = this.bones.length;
    const p = s.poses;
    for (let i = 0; i < n; i++) {
      const a = (f0 * n + i) * 7;
      const b = (f1 * n + i) * 7;
      const bone = this.bones[i];
      bone.position.set(
        p[a] + (p[b] - p[a]) * t,
        p[a + 1] + (p[b + 1] - p[a + 1]) * t,
        p[a + 2] + (p[b + 2] - p[a + 2]) * t,
      );
      THREE.Quaternion.slerpFlat(
        tmpQ as unknown as number[],
        0,
        p as unknown as number[],
        a + 3,
        p as unknown as number[],
        b + 3,
        t,
      );
      bone.quaternion.set(tmpQ[0], tmpQ[1], tmpQ[2], tmpQ[3]);
    }
  }

  dispose() {
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
  }
}
